// Modal para vincular manualmente una NC / retención "Sin factura destino" a la
// factura correcta de cartera (cuando la contraparte citó una serie equivocada).
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { PanelIntegrado } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const usd = (v: number | string | null) =>
  v == null ? '—' : Number(v).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
const inputCls = 'h-9 rounded-md border border-input bg-background px-2 text-sm';

interface FacturaPick {
  id: string; serie_comprobante: string | null; partner: string | null;
  identificacion: string | null; importe_total: number;
}

export function VincularFacturaModal({ facturasRpc, contraparte, detalle, vinculando, onClose, onPick }: {
  facturasRpc: 'cxc_listar' | 'cxp_listar';
  contraparte: string;
  detalle: string;            // texto del documento que se está vinculando
  vinculando: boolean;        // mutación en curso
  onClose: () => void;
  onPick: (ticketId: string) => void;
}) {
  const [q, setQ] = useState('');
  const [aplicado, setAplicado] = useState('');
  const { data, isFetching } = useQuery<{ documentos: FacturaPick[] }>({
    queryKey: ['vincFacturas', facturasRpc, aplicado],
    queryFn: async () =>
      (await apiClient.post(`/rpc/${facturasRpc}`, { _estado: 'todas', _serie: aplicado || null }, FIN)).data,
    enabled: aplicado.trim().length > 0,
  });
  const facturas = data?.documentos ?? [];

  return (
    <PanelIntegrado title="Vincular factura" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">{detalle}</p>
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); setAplicado(q.trim()); }}>
            <input className={`${inputCls} flex-1`} placeholder="Buscar factura por serie (ej. 1280)…"
              value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
            <Button type="submit" size="sm" variant="outline"><Search className="w-4 h-4" /></Button>
          </form>

          {aplicado.trim().length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Escribe parte de la serie de la factura y busca.</p>
          ) : isFetching ? (
            <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
          ) : facturas.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Sin facturas para “{aplicado}”.</p>
          ) : (
            <div className="overflow-x-auto max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3">Serie</th>
                    <th className="py-2 pr-3">{contraparte}</th>
                    <th className="py-2 pr-3 text-right">Importe</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {facturas.map((f) => (
                    <tr key={f.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="py-2 pr-3 whitespace-nowrap font-medium">{f.serie_comprobante || '—'}</td>
                      <td className="py-2 pr-3"><p className="truncate max-w-56" title={f.partner ?? ''}>{f.partner || '—'}</p></td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(f.importe_total)}</td>
                      <td className="py-2 text-right">
                        <Button size="sm" isLoading={vinculando} onClick={() => onPick(f.id)}>Elegir</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </PanelIntegrado>
  );
}
